/* eslint-disable @typescript-eslint/no-require-imports */
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { NavigationContainer } from '@react-navigation/native'
import { Navigation } from './src/navigator/Navigator'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider } from './src/context/Config/ConfigContext'
import { AuthProvider } from './src/context/Auth/AuthContext'
import { SeaWaterProvider } from './src/context/SeaWater/SeaWaterContext'
import { FreshWaterProvider } from './src/context/FreshWater/FreshWaterContext'
import { AlertsProvider } from './src/context/Alerts/AlertsContext'
import i18next from 'i18next'
import { I18nextProvider } from 'react-i18next'
import * as Notifications from 'expo-notifications'
import { NecropsyProvider } from './src/context/Necropsy/Provider'
import { FuelProvider } from './src/context/Logistics/fuel/FuelProvider'
import { useFonts } from 'expo-font'
import * as SplashScreen from 'expo-splash-screen'
import { Appearance, Platform, StyleSheet } from 'react-native'

SplashScreen.preventAutoHideAsync()
// Forzar modo claro en la capa JS independiente del modo del sistema operativo (solo nativo, web no soporta setColorScheme)
if (Platform.OS !== 'web') Appearance.setColorScheme('light')

import { useSslPinning } from './src/hooks/useSslPinning'

const queryClient = new QueryClient()
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: false,
    shouldSetBadge: false,
  }),
})

const AppState = ({ children }: PropsWithChildren) => {
  return (
    <ConfigProvider>
      <AuthProvider>
        <FreshWaterProvider>
          <SeaWaterProvider>
            <NecropsyProvider>
              <FuelProvider>
                <AlertsProvider>{children}</AlertsProvider>
              </FuelProvider>
            </NecropsyProvider>
          </SeaWaterProvider>
        </FreshWaterProvider>
      </AuthProvider>
    </ConfigProvider>
  )
}

const App = () => {
  const [fontsLoaded, fontError] = useFonts({
    Dobra: require('./src/assets/static/Dobra-Medium.ttf'),
    'Dobra-Bold': require('./src/assets/static/Dobra-Bold.ttf'),
    'Dobra-Book': require('./src/assets/static/Dobra-Book.ttf'),
  })
  const [fontTimeout, setFontTimeout] = useState(false)
  useSslPinning()

  useEffect(() => {
    const timer = setTimeout(() => setFontTimeout(true), 5000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (fontsLoaded || fontError || fontTimeout) {
      SplashScreen.hideAsync()
    }
  }, [fontsLoaded, fontError, fontTimeout])

  if (!fontsLoaded && !fontError && !fontTimeout) return null

  return (
    <GestureHandlerRootView style={styles.root}>
      <SafeAreaProvider>
        <NavigationContainer
          documentTitle={
            Platform.OS === 'web'
              ? {
                  formatter: () => `© Gestión AquaChile App ${new Date().getFullYear()}`,
                }
              : undefined
          }
        >
          <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18next}>
              <AppState>
                <Navigation />
              </AppState>
            </I18nextProvider>
          </QueryClientProvider>
        </NavigationContainer>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  )
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
})

export default App
